/**
 * MoniteurLayout — Espace enseignant / moniteur d’auto-école (Phase 32 — épuré).
 *
 * Sidebar minimaliste : 7 items essentiels uniquement (cf. demande utilisateur).
 * Style codevoiture : fond sombre #0a0a0b, sidebar #0d0d0e, brand rouge/rose.
 * Route : /moniteur/*   Rôles : enseignant | admin.
 */
import React, { useEffect, useState } from 'react';
import { Outlet, Link, useLocation, useNavigate } from 'react-router-dom';
import {
  Calendar, Users, IdCard, QrCode, Globe, Clapperboard,
  LogOut, ExternalLink, GraduationCap, Contact,
} from 'lucide-react';
import { useApp } from '../contexts/AppContext';
import { RefreshButton } from './RefreshButton';
import { BottomNav } from './BottomNav';
import api from '../api';

// ============================================================
// Navigation ultra-simplifiée : 7 items essentiels uniquement.
// Cf. demande utilisateur — plus de bruit dans le back-office.
// ============================================================
// Back-office enseignant = outils métier uniquement.
// Les cours sont accessibles depuis le site public (Magazine, /bibliotheque).
const NAV = [
  { to: '/moniteur/planning', label: 'Planning', icon: Calendar, testid: 'mo-nav-planning' },
  { to: '/moniteur/eleves', label: 'Mes élèves', icon: Users, testid: 'mo-nav-eleves', badgeKey: 'eleves' },
  { to: '/moniteur/shorts', label: 'Shorts élèves', icon: Clapperboard, testid: 'mo-nav-shorts', badgeKey: 'shorts', highlight: true },
  { to: '/moniteur/ma-carte', label: 'Ma carte de visite', icon: Contact, testid: 'mo-nav-ma-carte' },
  { to: '/moniteur/ma-fiche', label: 'Ma fiche enseignant', icon: IdCard, testid: 'mo-nav-ma-fiche' },
  { to: '/moniteur/ma-fiche?tab=qr', label: 'Mon QR élève', icon: QrCode, testid: 'mo-nav-qr' },
  { to: '/', label: 'Site public', icon: Globe, testid: 'mo-nav-site-public' },
];

export default function MoniteurLayout() {
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const { logout, user } = useApp() || {};
  const [counts, setCounts] = useState({ eleves: 0, bilans: 0, shorts: 0 });

  useEffect(() => {
    (async () => {
      try {
        const list = await api.boListMyEleves ? await api.boListMyEleves() : { count: 0 };
        setCounts(c => ({ ...c, eleves: list?.count || (list?.items?.length || 0) }));
      } catch (_) { /* silent */ }
      try {
        const myShorts = await api.teacherShortsList('pending');
        setCounts(c => ({ ...c, shorts: Array.isArray(myShorts) ? myShorts.length : 0 }));
      } catch (_) { /* silent */ }
    })();
  }, [pathname]);

  const onLogout = () => { if (logout) logout(); navigate('/login'); };

  return (
    <div className="min-h-screen bg-[#0a0a0b] flex flex-col lg:flex-row" data-testid="moniteur-layout">
      {/* ===== SIDEBAR ===== */}
      <aside className="lg:w-60 lg:min-h-screen shrink-0 border-b lg:border-b-0 lg:border-r border-white/10 bg-[#0d0d0e] flex lg:flex-col">
        <div className="hidden lg:flex items-center gap-2.5 px-5 h-16 border-b border-white/10">
          <div className="w-9 h-9 rounded-xl bg-rose-600 text-white flex items-center justify-center shadow-lg shadow-rose-900/40">
            <GraduationCap size={16} />
          </div>
          <div>
            <div className="text-[9.5px] uppercase tracking-[0.22em] text-rose-400 font-bold">code2roues.fr</div>
            <div className="text-white text-[14px] font-semibold leading-tight">Espace enseignant</div>
          </div>
        </div>
        <nav className="flex lg:flex-col gap-1 p-2 lg:p-3 overflow-x-auto lg:overflow-visible flex-1 items-center lg:items-stretch" data-testid="mo-sidebar-nav">
          {NAV.map((n) => {
            const Icon = n.icon;
            const active = n.external ? false : (n.exact ? pathname === n.to : pathname.startsWith(n.to));
            const count = n.badgeKey ? counts[n.badgeKey] : 0;
            const highlightClass = n.highlight && !active
              ? 'bg-gradient-to-r from-rose-500/15 to-transparent border border-rose-500/25 text-white hover:from-rose-500/25'
              : '';
            const commonClass = `flex items-center gap-2.5 px-3.5 h-10 rounded-xl text-[13px] font-semibold whitespace-nowrap transition ${
              active ? 'bg-rose-600 text-white shadow-md shadow-rose-900/40' : highlightClass || 'text-white/65 hover:bg-white/5 hover:text-white'
            }`;
            const content = (
              <>
                <Icon size={15} className="shrink-0" />
                <span className="flex-1">{n.label}</span>
                {n.external && (
                  <ExternalLink size={11} className="shrink-0 text-white/40" />
                )}
                {count > 0 && (
                  <span className={`min-w-[20px] h-5 px-1.5 rounded-full text-[10.5px] font-bold inline-flex items-center justify-center ${active ? 'bg-white text-rose-600' : 'bg-rose-600 text-white'}`}>
                    {count}
                  </span>
                )}
              </>
            );
            // Rendu Link simple pour tous les items (plus de openInBrowser complexe).
            const externalProps = n.external
              ? { target: '_blank', rel: 'noopener noreferrer' }
              : {};
            return (
              <Link
                key={n.to}
                to={n.to}
                {...externalProps}
                data-testid={n.testid}
                className={commonClass}
              >
                {content}
              </Link>
            );
          })}
        </nav>
        <div className="hidden lg:block p-3 border-t border-white/10 space-y-1">
          <Link to="/" data-testid="mo-back-to-site" className="flex items-center gap-2.5 px-3.5 h-10 rounded-xl text-[13px] font-semibold text-white/60 hover:bg-white/5 hover:text-white transition">
            <ExternalLink size={14} /> Retour à l&apos;accueil
          </Link>
          <button onClick={onLogout} data-testid="mo-logout" className="w-full flex items-center gap-2.5 px-3.5 h-10 rounded-xl text-[13px] font-semibold text-white/65 hover:bg-red-950/40 hover:text-red-300 transition">
            <LogOut size={14} /> Déconnexion
          </button>
          {user && <div className="px-3.5 pt-2 pb-1 text-white/35 text-[11px] truncate">{user.email}</div>}
        </div>
      </aside>

      {/* ===== CONTENU ===== */}
      <div className="flex-1 min-w-0">
        <header className="h-12 lg:h-16 border-b border-white/10 bg-[#0d0d0e]/60 backdrop-blur flex items-center justify-between px-4 lg:px-8">
          <div className="text-white/45 text-[12px] uppercase tracking-[0.2em] truncate">Espace enseignant ECSR</div>
          <div className="flex items-center gap-2">
            <span
              className="hidden sm:inline-flex items-center gap-1.5 px-2.5 h-6 rounded-full bg-amber-500/15 border border-amber-500/40 text-amber-300 text-[10px] font-black uppercase tracking-[0.18em]"
              data-testid="moniteur-role-badge"
              title="Vous êtes connecté avec un compte moniteur Code 2 Roues"
            >
              <GraduationCap size={11} />
              Compte moniteur
            </span>
            <RefreshButton variant="moniteur" />
            <div className="hidden lg:flex items-center gap-2 text-white/60 text-[12px]">
              <GraduationCap size={12} />
              <span>{user?.name || user?.email?.split('@')[0] || 'Enseignant'}</span>
            </div>
            <button onClick={onLogout} className="lg:hidden text-red-300 text-[12px] inline-flex items-center gap-1">
              <LogOut size={12} /> Quitter
            </button>
          </div>
        </header>
        <main className="px-4 lg:px-8 py-6 lg:py-8 pb-24 lg:pb-8" data-testid="mo-main">
          <Outlet />
        </main>
      </div>
      {/* BottomNav mobile — accès rapide Plateau / Circu / Tutos / Mag depuis le BO */}
      <BottomNav />
    </div>
  );
}
